import { TFunction } from 'i18next';

import { BillingSewBillingTypeEnum } from '@/backoffice/types/types.generated';
import { getEnumValues } from '@/shared/utils/enum/get-enum-options';

// Cost-center billing types: the SEW Order Number is optional for them, and the
// customer fields hold a cost center rather than an SAP customer.
export const SEW_COST_CENTER_BILLING_TYPES: BillingSewBillingTypeEnum[] = [
  BillingSewBillingTypeEnum.InternalCostCenter,
  BillingSewBillingTypeEnum.ProjectCostCenter,
];

// An unset billing type is treated as Customer Billing, matching the field's
// default and the hint behaviour below.
export const isSewCostCenterBillingType = (billingType?: string) =>
  SEW_COST_CENTER_BILLING_TYPES.includes(
    billingType as BillingSewBillingTypeEnum,
  );

const getBillingTypeLabelKey = (billingType: string) =>
  `backoffice:sew.onboarding.billing_types.${billingType.toLowerCase()}`;

export const getSewBillingTypeOptions = (t: TFunction) =>
  getEnumValues(BillingSewBillingTypeEnum).map((value) => ({
    value,
    label: t(getBillingTypeLabelKey(value)),
  }));

// Unknown values fall back to the raw metadata so nothing renders as a key.
export const getSewBillingTypeLabel = (
  t: TFunction,
  billingType?: string | null,
) => {
  if (!billingType) return billingType;

  const isKnownType = getEnumValues(BillingSewBillingTypeEnum).includes(
    billingType as BillingSewBillingTypeEnum,
  );

  return isKnownType ? t(getBillingTypeLabelKey(billingType)) : billingType;
};

// i18n suffix for the cost-center-specific field hints; undefined for Customer
// Billing (and any unset value), meaning no hint should be shown.
export const getSewCostCenterHintSuffix = (billingType?: string) => {
  if (billingType === BillingSewBillingTypeEnum.InternalCostCenter)
    return 'internal';
  if (billingType === BillingSewBillingTypeEnum.ProjectCostCenter)
    return 'project';
  return undefined;
};
